import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { ThreeDots } from 'react-loader-spinner';
import '../css/oneShotResume.css';
import { AI_FUNCTIONS_BASE_URL as FUNCTIONS_BASE_URL } from '../config/api';
import updateTokenUsage from './updateTokenUsage';
import { buildOneShotResumeLexicon, clean, uniqCI } from './oneShotResumeLexicon';
import { splitHighlightedText } from './responsibilitySuggestionUtils';
import { normalizeResumeBulletLines } from './resumeBulletUtils';
import {
  buildSkillContextFromForm,
  curateOneShotSkills,
  inferTransferableSkillsForOneShot,
} from './skillCuration';
import { normalizeResumeFormat, RESUME_FORMATS } from './resumeFormat';

const isVisible = (entry) => entry?.showOnResume !== false;

const withVisibilityDefaults = (data = {}) => ({
  ...data,
  workHistory: Array.isArray(data.workHistory)
    ? data.workHistory.map((job) => ({
        ...job,
        showOnResume: job?.showOnResume !== false,
      }))
    : [],
  educationHistory: Array.isArray(data.educationHistory)
    ? data.educationHistory.map((edu) => ({
        ...edu,
        showOnResume: edu?.showOnResume !== false,
      }))
    : [],
});

const buildGroundedResumePayload = (data = {}) => {
  const normalized = withVisibilityDefaults(data);
  return {
    ...normalized,
    workHistory: normalized.workHistory.filter(isVisible),
    educationHistory: normalized.educationHistory.filter(isVisible),
  };
};

const collectProfileText = (value) => {
  if (Array.isArray(value)) return value.flatMap(collectProfileText);
  if (value && typeof value === 'object') return Object.values(value).flatMap(collectProfileText);
  return clean(value) ? [clean(value)] : [];
};

const extractTargetOrganizationNames = (jobDescription = '') => {
  const text = clean(jobDescription);
  if (!text) return [];
  const patterns = [
    /\b([A-Z][A-Za-z0-9&.'-]*(?:\s+[A-Z][A-Za-z0-9&.'-]*){0,4})\s+(?:is|are)\s+(?:seeking|hiring|looking for)\b/g,
    /\b(?:at|with|for)\s+([A-Z][A-Za-z0-9&.'-]*(?:\s+[A-Z][A-Za-z0-9&.'-]*){0,4})\b/g,
  ];
  const blockedWords = new Set(['A', 'An', 'The', 'This', 'That', 'Successful', 'Federal', 'Private']);
  return uniqCI(patterns.flatMap((pattern) => (
    [...text.matchAll(pattern)]
      .map((match) => clean(match[1]).replace(/[.,;:]+$/, ''))
      .filter((name) => {
        const words = name.split(/\s+/);
        return words.length <= 5 && !blockedWords.has(words[0]);
      })
  )));
};

const getGroundedProfessionalSummary = ({
  generatedSummary,
  sourceSummary,
  sourceResume,
  jobDescription,
  warnings,
}) => {
  const summary = clean(generatedSummary);
  if (!summary) return sourceSummary;

  const sourceText = collectProfileText(sourceResume).join(' ').toLowerCase();
  const lowerSummary = summary.toLowerCase();
  const unsupportedTargetOrganizations = extractTargetOrganizationNames(jobDescription)
    .filter((name) => (
      lowerSummary.includes(name.toLowerCase())
      && !sourceText.includes(name.toLowerCase())
    ));

  if (unsupportedTargetOrganizations.length) {
    warnings.push('One-Shot kept your saved professional summary because the generated summary appeared to claim affiliation with a target organization.');
    return sourceSummary;
  }

  return summary;
};

const isOneShotBrowserDebugEnabled = () => (
  process.env.NODE_ENV !== 'production'
  || ['localhost', '127.0.0.1'].includes(window.location.hostname)
);

const buildOneShotDebugSummary = ({
  endpoint,
  userID,
  groundedResumeData,
  jobDescription,
  resumeMode,
  resumeLexicon,
}) => ({
  endpoint,
  prompt: 'generateOneShotResume',
  requestId: null,
  hasUserID: Boolean(clean(userID)),
  userIDPreview: clean(userID) ? `${clean(userID).slice(0, 3)}...` : '',
  mode: resumeMode,
  jobDescriptionLength: clean(jobDescription).length,
  workHistoryCount: Array.isArray(groundedResumeData?.workHistory)
    ? groundedResumeData.workHistory.length
    : 0,
  educationHistoryCount: Array.isArray(groundedResumeData?.educationHistory)
    ? groundedResumeData.educationHistory.length
    : 0,
  profileSkillCount: [
    ...(Array.isArray(groundedResumeData?.skills) ? groundedResumeData.skills : []),
    ...(Array.isArray(groundedResumeData?.selectedSkills) ? groundedResumeData.selectedSkills : []),
  ].filter(Boolean).length,
  evidenceSignalCount: Array.isArray(resumeLexicon?.evidenceSignals)
    ? resumeLexicon.evidenceSignals.length
    : 0,
  lexiconKeywordCount: [
    ...(Array.isArray(resumeLexicon?.verifiedKeywords) ? resumeLexicon.verifiedKeywords : []),
    ...(Array.isArray(resumeLexicon?.transferableKeywords) ? resumeLexicon.transferableKeywords : []),
    ...(Array.isArray(resumeLexicon?.targetKeywords) ? resumeLexicon.targetKeywords : []),
  ].filter(Boolean).length,
});

const mergeGeneratedWorkWithSource = (generatedWork = [], sourceWork = []) => {
  let generatedIndex = 0;
  return sourceWork.map((sourceJob) => {
    if (!isVisible(sourceJob)) return sourceJob;
    const generatedJob = generatedWork[generatedIndex] || {};
    generatedIndex += 1;
    const identityFields = ['jobTitle', 'employer', 'employerAddress', 'startDate', 'endDate'];
    const hasIdentityMismatch = identityFields.some((field) => (
      clean(generatedJob[field]) && clean(generatedJob[field]) !== clean(sourceJob[field])
    ));
    if (hasIdentityMismatch) {
      return {
        ...sourceJob,
        showOnResume: true,
      };
    }
    return {
      ...sourceJob,
      enhancedResponsibilities: generatedJob.enhancedResponsibilities ?? sourceJob.enhancedResponsibilities,
      enhancedAccomplishments: generatedJob.enhancedAccomplishments ?? sourceJob.enhancedAccomplishments,
      showOnResume: true,
    };
  });
};

const mergeGeneratedEducationWithSource = (generatedEducation = [], sourceEducation = []) => {
  let generatedIndex = 0;
  return sourceEducation.map((sourceEducationEntry) => {
    if (!isVisible(sourceEducationEntry)) return sourceEducationEntry;
    const generatedEntry = generatedEducation[generatedIndex] || {};
    generatedIndex += 1;
    return {
      ...sourceEducationEntry,
      enhancedProjects: generatedEntry.enhancedProjects ?? sourceEducationEntry.enhancedProjects,
      relevantCoursesBullets: generatedEntry.relevantCoursesBullets ?? sourceEducationEntry.relevantCoursesBullets,
      showOnResume: true,
    };
  });
};

const hasGeneratedWorkEnhancement = (job = {}) => (
  normalizeResumeBulletLines(job.enhancedResponsibilities).length > 0
  || normalizeResumeBulletLines(job.enhancedAccomplishments).length > 0
);

const hasGeneratedEducationEnhancement = (education = {}) => (
  normalizeResumeBulletLines(education.enhancedProjects).length > 0
  || normalizeResumeBulletLines(education.relevantCoursesBullets).length > 0
);

const applyPrivateRelevantOnlyFilter = (draft = {}) => ({
  ...draft,
  workHistory: Array.isArray(draft.workHistory)
    ? draft.workHistory.map((job) => (
        isVisible(job)
          ? { ...job, showOnResume: hasGeneratedWorkEnhancement(job) }
          : job
      ))
    : draft.workHistory,
  educationHistory: Array.isArray(draft.educationHistory)
    ? draft.educationHistory.map((education) => (
        isVisible(education)
          ? { ...education, showOnResume: hasGeneratedEducationEnhancement(education) }
          : education
      ))
    : draft.educationHistory,
});

const enforceGroundedDraft = (responseData, sourceResume) => {
  if (!responseData?.resumeData) return responseData;
  const normalizedSource = withVisibilityDefaults(sourceResume);
  const groundedSource = buildGroundedResumePayload(sourceResume);
  const generated = responseData.resumeData;
  const oneShotJobAnalysis = responseData.jobAnalysis || {};
  const oneShotJobDescription = responseData.jobDescription || '';
  const aiInferredResumeTitle = clean(
    responseData.suggestedResumeTitle
    || generated.aiInferredResumeTitle
    || normalizedSource.aiInferredResumeTitle,
  );
  const skillContext = buildSkillContextFromForm(normalizedSource, {
    ...oneShotJobAnalysis,
    jobDescription: oneShotJobDescription,
  });
  const warnings = [
    ...(Array.isArray(responseData.warnings) ? responseData.warnings : []),
  ];
  const curatedSkills = curateOneShotSkills({
    generatedSkills: uniqCI([
      ...(Array.isArray(generated.skills) ? generated.skills : []),
      ...(Array.isArray(generated.selectedSkills) ? generated.selectedSkills : []),
      ...(Array.isArray(responseData.inferredSkills) ? responseData.inferredSkills : []),
      ...(Array.isArray(responseData.localInferredSkills) ? responseData.localInferredSkills : []),
    ]),
    profileSkills: uniqCI([
      ...(Array.isArray(normalizedSource.selectedSkills) ? normalizedSource.selectedSkills : []),
      ...(Array.isArray(normalizedSource.skills) ? normalizedSource.skills : []),
    ]),
    context: skillContext,
  });

  if ((generated.workHistory || []).length > groundedSource.workHistory.length) {
    warnings.push('One-Shot removed generated work entries that were not present in your saved work history.');
  }
  if ((generated.educationHistory || []).length > groundedSource.educationHistory.length) {
    warnings.push('One-Shot removed generated education entries that were not present in your saved education history.');
  }
  const professionalSummary = getGroundedProfessionalSummary({
    generatedSummary: generated.professionalSummary,
    sourceSummary: normalizedSource.professionalSummary,
    sourceResume: groundedSource,
    jobDescription: oneShotJobDescription,
    warnings,
  });

  return {
    ...responseData,
    resumeData: {
      ...normalizedSource,
      user: normalizedSource.user,
      oneShotJobAnalysis,
      oneShotJobDescription,
      aiInferredResumeTitle,
      professionalSummary,
      showProfessionalSummary: generated.showProfessionalSummary ?? normalizedSource.showProfessionalSummary,
      skills: curatedSkills,
      selectedSkills: curatedSkills,
      workHistory: mergeGeneratedWorkWithSource(generated.workHistory, normalizedSource.workHistory),
      educationHistory: mergeGeneratedEducationWithSource(
        generated.educationHistory,
        normalizedSource.educationHistory,
      ),
    },
    warnings: uniqCI(warnings),
  };
};

const GENERATION_STAGES = {
  idle: { label: '', progress: 0 },
  preparing: { label: 'Preparing profile evidence', progress: 10 },
  drafting: { label: 'Drafting federal resume', progress: 48 },
  finalizing: { label: 'Validating draft', progress: 88 },
  complete: { label: 'Draft ready', progress: 100 },
};

const flattenMatchedTerms = (matchedKeywords) => {
  if (!matchedKeywords) return [];
  if (Array.isArray(matchedKeywords)) {
    return uniqCI(matchedKeywords.map((item) => (
      typeof item === 'string' ? item : item?.term || item?.keyword || item?.text
    )));
  }
  if (typeof matchedKeywords === 'object') {
    return uniqCI(Object.values(matchedKeywords).flatMap(flattenMatchedTerms));
  }
  return [];
};

const flattenUnmatchedTerms = (unmatchedKeywords) => {
  if (!Array.isArray(unmatchedKeywords)) return [];
  return uniqCI(unmatchedKeywords.map((item) => (
    typeof item === 'string' ? item : item?.term || item?.keyword || item?.text
  )));
};

const highlightText = (text, terms) => {
  const value = clean(text);
  const cleanTerms = uniqCI(terms).filter((term) => value.toLowerCase().includes(term.toLowerCase()));
  if (!value || !cleanTerms.length) return value;
  if (cleanTerms.length === 1) {
    return splitHighlightedText(value, cleanTerms[0]).map((part, index) => (
      part.highlighted
        ? <mark className="keyword-highlight verified" key={index}>{part.text}</mark>
        : <React.Fragment key={index}>{part.text}</React.Fragment>
    ));
  }
  const escaped = cleanTerms
    .sort((a, b) => b.length - a.length)
    .map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escaped.join('|')})`, 'gi');
  return value.split(regex).filter(Boolean).map((part, index) => (
    cleanTerms.some((term) => term.toLowerCase() === part.toLowerCase())
      ? <mark className="keyword-highlight verified" key={index}>{part}</mark>
      : <React.Fragment key={index}>{part}</React.Fragment>
  ));
};

const collectWorkBullets = (job = {}) => {
  const enhanced = [
    ...(Array.isArray(job.enhancedResponsibilities) ? job.enhancedResponsibilities : []),
    ...(Array.isArray(job.enhancedAccomplishments) ? job.enhancedAccomplishments : []),
  ];
  const normalizedEnhanced = normalizeResumeBulletLines(enhanced);
  if (normalizedEnhanced.length) return normalizedEnhanced;
  return normalizeResumeBulletLines([
    ...(Array.isArray(job.responsibilitiesBullets) ? job.responsibilitiesBullets : []),
    ...(Array.isArray(job.accomplishmentsBullets) ? job.accomplishmentsBullets : []),
    job.responsibilities,
    job.keyAccomplishments,
  ]);
};

const needsProfileMessage = 'Add profile information in the Form before generating a one-shot resume.';

const KeywordSummary = ({ matchedTerms, unmatchedTerms }) => (
  <div className="one-shot-keyword-summary" aria-label="Keyword match summary">
    <div>
      <strong>{matchedTerms.length}</strong>
      <span>Matched</span>
    </div>
    <div>
      <strong>{unmatchedTerms.length}</strong>
      <span>Missing</span>
    </div>
  </div>
);

const addKeywordToDraft = (draft, term) => {
  if (!draft || !clean(term)) return draft;
  const normalizedTerm = clean(term);
  const currentSkills = Array.isArray(draft.skills) ? draft.skills : [];
  const currentSelectedSkills = Array.isArray(draft.selectedSkills) ? draft.selectedSkills : [];
  return {
    ...draft,
    skills: uniqCI([...currentSkills, normalizedTerm]),
    selectedSkills: uniqCI([...currentSelectedSkills, normalizedTerm]),
  };
};

const DraftResumeReview = ({ draft, matchedTerms }) => {
  if (!draft) return null;
  const skills = Array.isArray(draft.selectedSkills) && draft.selectedSkills.length
    ? draft.selectedSkills
    : (Array.isArray(draft.skills) ? draft.skills : []);
  const workHistory = Array.isArray(draft.workHistory)
    ? draft.workHistory.filter((job) => job?.showOnResume !== false)
    : [];

  return (
    <div className="one-shot-draft-review">
      <div className="one-shot-review-title">
        <h2>Generated Draft Review</h2>
        <span>Review only</span>
      </div>
      <p className="one-shot-note">Keyword marks stay out of DOCX export.</p>

      {clean(draft.professionalSummary) && (
        <section>
          <h3>Professional Summary</h3>
          <p>{highlightText(draft.professionalSummary, matchedTerms)}</p>
        </section>
      )}

      {skills.length > 0 && (
        <section>
          <h3>Skills</h3>
          <div className="one-shot-skill-list">
            {skills.map((skill, index) => {
              const isMatched = matchedTerms.some((term) => term.toLowerCase() === clean(skill).toLowerCase());
              return (
                <span className={`one-shot-skill ${isMatched ? 'matched' : ''}`} key={`${skill}-${index}`}>
                  {highlightText(skill, matchedTerms)}
                </span>
              );
            })}
          </div>
        </section>
      )}

      {workHistory.length > 0 && (
        <section>
          <h3>Work Experience</h3>
          {workHistory.map((job, index) => {
            const header = [job.jobTitle, job.employer].map(clean).filter(Boolean).join(' | ');
            const bullets = collectWorkBullets(job);
            return (
              <div className="one-shot-work-entry" key={`${header}-${index}`}>
                {header && <strong>{highlightText(header, matchedTerms)}</strong>}
                {bullets.length > 0 && (
                  <ul>
                    {bullets.map((bullet, bulletIndex) => (
                      <li key={`${bullet}-${bulletIndex}`}>{highlightText(bullet, matchedTerms)}</li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </section>
      )}
    </div>
  );
};

export default function OneShotResume({
  formData,
  setFormData,
  setTokenUsage,
  setUsagePercentage,
  tokenThreshold,
}) {
  const savedOneShotState = formData?.oneShotResumeState || {};
  const resumeMode = normalizeResumeFormat(formData?.resumeFormat);
  const isFederalMode = resumeMode === RESUME_FORMATS.FEDERAL;
  const [jobDescription, setJobDescription] = useState(
    savedOneShotState.jobDescription || formData?.oneShotJobDescription || '',
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStage, setGenerationStage] = useState('idle');
  const [progressHint, setProgressHint] = useState(0);
  const [error, setError] = useState(savedOneShotState.error || '');
  const [draftResponse, setDraftResponse] = useState(savedOneShotState.draftResponse || null);
  const [autoGeneratedSkills, setAutoGeneratedSkills] = useState(savedOneShotState.autoGeneratedSkills || []);
  const [privateRelevantOnly, setPrivateRelevantOnly] = useState(Boolean(savedOneShotState.privateRelevantOnly));
  const [localWarnings, setLocalWarnings] = useState(savedOneShotState.localWarnings || []);
  const [applyMessage, setApplyMessage] = useState(savedOneShotState.applyMessage || '');

  const persistOneShotState = React.useCallback((updates) => {
    if (!setFormData) return;
    setFormData((previousFormData = {}) => ({
      ...previousFormData,
      oneShotResumeState: {
        ...(previousFormData.oneShotResumeState || {}),
        ...updates,
        updatedAt: new Date().toISOString(),
      },
    }));
  }, [setFormData]);

  const updateJobDescription = React.useCallback((value) => {
    setJobDescription(value);
    persistOneShotState({ jobDescription: value });
  }, [persistOneShotState]);

  const updateError = React.useCallback((value) => {
    setError(value);
    persistOneShotState({ error: value });
  }, [persistOneShotState]);

  const updateDraftResponse = React.useCallback((value) => {
    setDraftResponse(value);
    persistOneShotState({ draftResponse: value });
  }, [persistOneShotState]);

  const updateAutoGeneratedSkills = React.useCallback((value) => {
    setAutoGeneratedSkills(value);
    persistOneShotState({ autoGeneratedSkills: value });
  }, [persistOneShotState]);

  const updatePrivateRelevantOnly = React.useCallback((value) => {
    setPrivateRelevantOnly(value);
    persistOneShotState({ privateRelevantOnly: value });
  }, [persistOneShotState]);

  const updateLocalWarnings = React.useCallback((value) => {
    setLocalWarnings(value);
    persistOneShotState({ localWarnings: value });
  }, [persistOneShotState]);

  const updateApplyMessage = React.useCallback((value) => {
    setApplyMessage(value);
    persistOneShotState({ applyMessage: value });
  }, [persistOneShotState]);

  const draft = draftResponse?.resumeData || null;
  const matchedTerms = useMemo(
    () => flattenMatchedTerms(draftResponse?.matchedKeywords),
    [draftResponse],
  );
  const unmatchedTerms = useMemo(
    () => flattenUnmatchedTerms(draftResponse?.unmatchedKeywords),
    [draftResponse],
  );
  const missingFields = Array.isArray(draftResponse?.missingProfileFields)
    ? draftResponse.missingProfileFields
    : [];
  const warnings = uniqCI([
    ...localWarnings,
    ...(Array.isArray(draftResponse?.warnings) ? draftResponse.warnings : []),
  ]);
  const profileLexicon = useMemo(
    () => buildOneShotResumeLexicon(buildGroundedResumePayload(formData), {}),
    [formData],
  );
  const hasProfileEvidence = profileLexicon.evidenceSignals.length > 0;
  const stage = GENERATION_STAGES[generationStage] || GENERATION_STAGES.idle;
  const generationProgress = isGenerating
    ? Math.min(Math.max(stage.progress, progressHint), 96)
    : stage.progress;

  React.useEffect(() => {
    if (!isGenerating) return undefined;
    const timer = window.setInterval(() => {
      setProgressHint((current) => Math.min(current + 2, 92));
    }, 1800);
    return () => window.clearInterval(timer);
  }, [isGenerating]);

  const refreshUsage = async () => {
    if (formData.user && tokenThreshold && setTokenUsage && setUsagePercentage) {
      await updateTokenUsage(formData.user, tokenThreshold, setTokenUsage, setUsagePercentage);
    }
  };

  const generateDraft = async () => {
    const trimmedDescription = jobDescription.trim();
    if (!trimmedDescription) {
      updateError(`Paste a job description before generating a ${isFederalMode ? 'federal' : 'private-sector'} resume draft.`);
      return;
    }
    if (!hasProfileEvidence) {
      updateError(needsProfileMessage);
      updateDraftResponse(null);
      updateAutoGeneratedSkills([]);
      updateLocalWarnings([]);
      return;
    }

    setIsGenerating(true);
    setGenerationStage('preparing');
    setProgressHint(0);
    updateError('');
    updateDraftResponse(null);
    updateAutoGeneratedSkills([]);
    updateLocalWarnings([]);
    updateApplyMessage('');

    try {
      const groundedResumeData = buildGroundedResumePayload(formData);
      const baseLexicon = buildOneShotResumeLexicon(groundedResumeData, {
        jobSummary: trimmedDescription.slice(0, 1200),
      });
      const generatedSkills = inferTransferableSkillsForOneShot(
        groundedResumeData,
        buildSkillContextFromForm(groundedResumeData, {
          jobDescription: trimmedDescription,
          jobSummary: trimmedDescription.slice(0, 1200),
        }),
      );
      const endpoint = `${FUNCTIONS_BASE_URL}/api/jobSeriesSearchHttp`;
      const requestId = `one-shot-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const debugSummary = buildOneShotDebugSummary({
        endpoint,
        userID: formData.user,
        groundedResumeData,
        jobDescription: trimmedDescription,
        resumeMode,
        resumeLexicon: baseLexicon,
      });
      debugSummary.requestId = requestId;

      setGenerationStage('drafting');
      setProgressHint(36);
      const startedAt = performance.now();
      if (isOneShotBrowserDebugEnabled()) {
        console.groupCollapsed(`[OneShotResume] ${requestId} request`);
        console.info('Request summary', debugSummary);
        console.info('Usage topology', {
          aiEndpoint: endpoint,
          saveLoadEndpointExpected: 'Configured in local function settings',
          browserOnly: true,
        });
        console.groupEnd();
      }
      const response = await axios.post(
        endpoint,
        {
          prompt: 'generateOneShotResume',
          userID: formData.user,
          resumeData: groundedResumeData,
          jobDescription: trimmedDescription,
          mode: resumeMode,
          resumeLexicon: baseLexicon,
          autoGeneratedSkills: generatedSkills,
          onlyRelevantEntries: !isFederalMode && privateRelevantOnly,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-client-request-id': requestId,
          },
        },
      );
      if (isOneShotBrowserDebugEnabled()) {
        console.groupCollapsed(`[OneShotResume] ${requestId} response`);
        console.info('Status', response.status);
        console.info('Elapsed ms', Math.round(performance.now() - startedAt));
        console.info('Response data', response.data);
        console.groupEnd();
      }

      setGenerationStage('finalizing');
      setProgressHint(88);
      updateLocalWarnings([]);
      const groundedResponse = enforceGroundedDraft(
        {
          ...response.data,
          jobDescription: trimmedDescription,
          localInferredSkills: generatedSkills,
        },
        formData,
      );
      updateDraftResponse(groundedResponse);
      updateAutoGeneratedSkills(
        Array.isArray(groundedResponse?.inferredSkills)
          ? groundedResponse.inferredSkills
          : [],
      );
      await refreshUsage();
      setGenerationStage('complete');
      setProgressHint(100);
    } catch (err) {
      if (isOneShotBrowserDebugEnabled()) {
        const status = err?.response?.status;
        const responseData = err?.response?.data;
        console.groupCollapsed('[OneShotResume] request failed');
        console.error('Error object', err);
        console.info('HTTP status', status || 'No HTTP status');
        console.info('Server response body', responseData || 'No response body');
        console.info('Request config', {
          url: err?.config?.url,
          method: err?.config?.method,
          timeout: err?.config?.timeout,
          headers: {
            'Content-Type': err?.config?.headers?.['Content-Type'],
            'x-client-request-id': err?.config?.headers?.['x-client-request-id'],
          },
        });
        console.groupEnd();
      } else {
        console.error('One-shot resume generation failed:', err);
      }
      if (err?.response?.status === 403) {
        updateError('Token limit exceeded. Please contact support for additional tokens.');
      } else {
        updateError('Failed to generate the federal resume draft. Please try again later.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const applyDraft = () => {
    if (!draft) return;
    const draftToApply = !isFederalMode && privateRelevantOnly
      ? applyPrivateRelevantOnlyFilter(draft)
      : draft;
    setFormData((previousFormData = {}) => ({
      ...previousFormData,
      ...draftToApply,
      user: previousFormData.user || formData.user,
    }));
    updateApplyMessage(
      !isFederalMode && privateRelevantOnly
        ? 'Draft applied with only enhanced work and education entries shown. It is still unsaved until you save it to a resume slot.'
        : 'Draft applied to the resume preview. It is still unsaved until you save it to a resume slot.',
    );
  };

  const addMissingKeywordToDraft = (term) => {
    if (!draft || !clean(term)) return;
    const normalizedTerm = clean(term);
    setDraftResponse((current) => {
      if (!current?.resumeData) return current;
      const next = {
        ...current,
        resumeData: addKeywordToDraft(current.resumeData, normalizedTerm),
        matchedKeywords: [
          ...(Array.isArray(current.matchedKeywords) ? current.matchedKeywords : []),
          { term: normalizedTerm, category: 'user-added' },
        ],
        unmatchedKeywords: flattenUnmatchedTerms(current.unmatchedKeywords)
          .filter((keyword) => keyword.toLowerCase() !== normalizedTerm.toLowerCase()),
        changeSummary: uniqCI([
          ...(Array.isArray(current.changeSummary) ? current.changeSummary : []),
          `Added "${normalizedTerm}" to draft skills at your request.`,
        ]),
      };
      persistOneShotState({ draftResponse: next });
      return next;
    });
    updateAutoGeneratedSkills(
      autoGeneratedSkills.filter(
        (skill) => clean(skill).toLowerCase() !== normalizedTerm.toLowerCase(),
      ),
    );
    updateApplyMessage('');
  };

  return (
    <div className="one-shot-resume-screen">
      <header className="one-shot-console-header">
        <div>
          <p className="one-shot-kicker">{isFederalMode ? 'Federal' : 'Private-sector'} resume console</p>
          <h1>One-Shot {isFederalMode ? 'Federal' : 'Private-Sector'} Resume</h1>
          <p className="one-shot-note">Generate an unsaved draft from profile evidence already in this app.</p>
        </div>
        <div className="one-shot-header-status">
          <span className={draft ? 'ready' : ''}>{draft ? 'Draft ready' : 'No draft yet'}</span>
          {draftResponse && <KeywordSummary matchedTerms={matchedTerms} unmatchedTerms={unmatchedTerms} />}
        </div>
      </header>

      <div className="one-shot-console-shell">
        <section className="one-shot-panel one-shot-control-panel">
          <div className="one-shot-panel-heading">
            <div>
              <span>Input</span>
              <h2>Job Description</h2>
            </div>
            <strong>{isFederalMode ? 'Federal' : 'Private'}</strong>
          </div>

          <label className="one-shot-field">
            <span>Job description</span>
            <textarea
              value={jobDescription}
              onChange={(event) => updateJobDescription(event.target.value)}
              placeholder={isFederalMode
                ? 'Paste the federal job description or USAJOBS announcement text here...'
                : 'Paste the private-sector job description here...'}
              rows={10}
            />
          </label>

          {!isFederalMode && (
            <label className="one-shot-option">
              <input
                type="checkbox"
                checked={privateRelevantOnly}
                onChange={(event) => updatePrivateRelevantOnly(event.target.checked)}
              />
              <span>
                <strong>Show only relevant entries after applying</strong>
                <small>Private resumes can hide work or education entries the draft did not use.</small>
              </span>
            </label>
          )}

          <div className="one-shot-control-footer">
            <button
              className={`one-shot-primary ${isGenerating ? 'generating' : ''}`}
              type="button"
              onClick={generateDraft}
              disabled={isGenerating || !hasProfileEvidence}
              aria-busy={isGenerating}
            >
              {isGenerating && (
                <span
                  className="one-shot-button-progress"
                  style={{ width: `${generationProgress}%` }}
                  aria-hidden="true"
                />
              )}
              {isGenerating ? (
                <span className="one-shot-loading">
                  <ThreeDots height="14" width="36" color="#ffffff" ariaLabel="loading" visible />
                  Generating draft...
                </span>
              ) : (
                `Generate ${isFederalMode ? 'Federal' : 'Private-Sector'} Resume Draft`
              )}
            </button>
            {isGenerating && (
              <div
                className="one-shot-generation-progress"
                role="progressbar"
                aria-label="One-shot resume generation progress"
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow={Math.round(generationProgress)}
              >
                <div>
                  <span>{stage.label}</span>
                  <strong>{Math.round(generationProgress)}%</strong>
                </div>
                <span className="one-shot-progress-track">
                  <span style={{ width: `${generationProgress}%` }} />
                </span>
              </div>
            )}

            {!hasProfileEvidence && (
              <p className="one-shot-profile-warning">
                Complete the Form first so One-Shot has verified profile details to use.
              </p>
            )}
            {error && <p className="one-shot-error" role="alert">{error}</p>}
          </div>
        </section>

        <section className="one-shot-panel one-shot-output-panel">
          <div className="one-shot-panel-heading">
            <div>
              <span>Output</span>
              <h2>Draft Review</h2>
            </div>
            <strong>{draft ? 'Unsaved' : 'Waiting'}</strong>
          </div>

          <div className="one-shot-output-scroll">
            {(autoGeneratedSkills.length > 0 || draftResponse) ? (
              <div className="one-shot-results">
                <div className="one-shot-result-header">
                  <div>
                    <h2>Draft Status</h2>
                    <p>This draft is not saved. Use the regular resume slot controls after applying it.</p>
                  </div>
                  <KeywordSummary matchedTerms={matchedTerms} unmatchedTerms={unmatchedTerms} />
                </div>

                {autoGeneratedSkills.length > 0 && (
                  <section>
                    <h3>Inferred Skills to Review</h3>
                    <p className="one-shot-keyword-note">
                      These are relevant possibilities, not verified profile facts. Add only skills you can support.
                    </p>
                    <div className="one-shot-skill-list">
                      {autoGeneratedSkills.map((skill) => (
                        <span className="keyword-badge aligned keyword-action" key={skill}>
                          {skill}
                          <button type="button" onClick={() => addMissingKeywordToDraft(skill)}>
                            Confirm skill
                          </button>
                        </span>
                      ))}
                    </div>
                  </section>
                )}

                {Array.isArray(draftResponse?.changeSummary) && draftResponse.changeSummary.length > 0 && (
                  <section>
                    <h3>Changes Made</h3>
                    <ul>
                      {draftResponse.changeSummary.map((item, index) => (
                        <li key={`${item}-${index}`}>{clean(item)}</li>
                      ))}
                    </ul>
                  </section>
                )}

                {unmatchedTerms.length > 0 && (
                  <section>
                    <h3>Missing Keywords</h3>
                    <p className="one-shot-keyword-note">
                      Add only keywords you can support with your profile evidence. Added terms go into this draft's skills list.
                    </p>
                    <div className="one-shot-keyword-list">
                      {unmatchedTerms.map((term) => (
                        <span className="keyword-badge aligned keyword-action" key={term}>
                          {term}
                          <button type="button" onClick={() => addMissingKeywordToDraft(term)}>
                            Add to draft skills
                          </button>
                        </span>
                      ))}
                    </div>
                  </section>
                )}

                {missingFields.length > 0 && (
                  <section>
                    <h3>Missing Federal Fields</h3>
                    <ul>
                      {missingFields.map((field, index) => (
                        <li key={`${field}-${index}`}>{typeof field === 'string' ? field : clean(field?.label || field?.path)}</li>
                      ))}
                    </ul>
                  </section>
                )}

                {warnings.length > 0 && (
                  <section>
                    <h3>Warnings</h3>
                    <ul>
                      {warnings.map((warning, index) => (
                        <li key={`${warning}-${index}`}>{warning}</li>
                      ))}
                    </ul>
                  </section>
                )}

                <DraftResumeReview draft={draft} matchedTerms={matchedTerms} />
              </div>
            ) : (
              <div className="one-shot-empty-state">
                <span>{hasProfileEvidence ? 'Ready' : 'Profile needed'}</span>
                <h2>
                  {hasProfileEvidence
                    ? (isFederalMode
                      ? 'Paste a federal announcement to begin.'
                      : 'Paste a private-sector job description to begin.')
                    : 'Complete the Form before One-Shot.'}
                </h2>
                <p>
                  {hasProfileEvidence
                    ? 'Generated skills, keyword matches, missing fields, and the draft review appear here.'
                    : 'One-Shot only writes from profile evidence already saved in this app.'}
                </p>
              </div>
            )}
          </div>

          {draft && (
            <div className="one-shot-panel-footer">
              <button className="one-shot-primary" type="button" onClick={applyDraft}>
                Apply Draft to Resume Preview
              </button>
              {applyMessage && (
                <p className="one-shot-apply-confirmation" role="status">
                  {applyMessage}
                </p>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
